import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DashboardLayout from '../../components/layout/DashboardLayout';
import AdminSidebar from '../../components/sidebar/AdminSidebar';
import StatusBadge from '../../components/StatusBadge';
import { useNotifications } from '../../hooks/useNotifications';
import ApprovalWorkflowDrawer from '../../components/dashboard/ApprovalWorkflowDrawer';
import {
  completePaymentRequest,
  fetchAdminPaymentRequests,
  approvePaymentRequest,
  rejectPaymentRequest,
} from '../../redux/adminPaymentSlice';
import { AppDispatch, RootState } from '../../redux/store';
import { paymentsApi } from '../../services/api';
import './AdminPaymentsPage.css';

const AdminPaymentsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const notifications = useNotifications();
  const { paymentRequests, loading } = useSelector((state: RootState) => state.adminPayment);

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchAdminPaymentRequests());
  }, [dispatch]);

  const handleApprove = async (requestId: string) => {
    try {
      const result = await dispatch(approvePaymentRequest(requestId)).unwrap();
      if (result.reapStatus && result.reapStatus !== 'sent') {
        notifications.warning(
          `Payment approved, but Reap status is ${result.reapStatus}. ${result.reapError || 'Check row details.'}`
        );
      } else {
        notifications.success('Payment request approved and sent to Reap.');
      }
    } catch (error) {
      console.error('Failed to approve payment request:', error);
      notifications.error('Failed to approve payment request. Please try again.');
    }
  };

  const handleReject = async (requestId: string, rejectionReason: string) => {
    try {
      await dispatch(rejectPaymentRequest({ paymentId: requestId, rejectionReason })).unwrap();
      notifications.success('Payment request rejected.');
    } catch (error) {
      console.error('Failed to reject payment request:', error);
      notifications.error('Failed to reject payment request. Please try again.');
    }
  };

  const handleDebitWallet = async (requestId: string) => {
    try {
      const result = await paymentsApi.debitWalletForPayment(requestId);
      if (!result.success) {
        notifications.error(result.data?.message || 'Failed to debit wallet. Please try again.');
        return;
      }

      notifications.success('Wallet debited successfully. Payment moved to processing.');

      dispatch(fetchAdminPaymentRequests());
    } catch (error) {
      console.error('Failed to debit wallet:', error);
      notifications.error('Failed to debit wallet. Please try again.');
    }
  };

  const handleRetryReap = async (requestId: string) => {
    try {
      const result = await paymentsApi.retryReapSubmission(requestId);
      const reapStatus = result.data?.reapStatus;
      const reapError = result.data?.reapError;

      if (reapStatus === 'sent') {
        notifications.success('Reap submission succeeded. You can now debit wallet.');
      } else {
        notifications.warning(
          `Reap retry status is ${reapStatus || 'unknown'}. ${reapError || 'Check row details.'}`
        );
      }

      dispatch(fetchAdminPaymentRequests());
    } catch (error) {
      console.error('Failed to retry Reap submission:', error);
      notifications.error('Failed to retry Reap submission. Please try again.');
    }
  };

  const handleReapAction = async (requestId: string, action: 'approve' | 'accept_quote' | 'cancel') => {
    try {
      const result = await paymentsApi.performReapAction(requestId, action);
      if (result.success) {
        notifications.success(`Reap action '${action}' completed successfully.`);
      } else {
        notifications.warning(result.message || `Reap action '${action}' returned with warnings.`);
      }
      dispatch(fetchAdminPaymentRequests());
    } catch (error) {
      console.error('Failed to perform Reap action:', error);
      notifications.error('Failed to perform Reap action. Please try again.');
    }
  };

  const handleComplete = async (requestId: string) => {
    try {
      await dispatch(completePaymentRequest(requestId)).unwrap();
      notifications.success('Payment marked as completed successfully.');
    } catch (error) {
      console.error('Failed to complete payment:', error);
      notifications.error('Failed to complete payment. Please try again.');
    }
  };

  const formatCurrency = (amount: number, currencyCode: string) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  const filteredPayments = paymentRequests.filter((request) => {
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    const matchesSearch =
      searchTerm === '' ||
      request.recipientCompany.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.accountNumber.includes(searchTerm);
    return matchesStatus && matchesSearch;
  });

  const selectedRequest = filteredPayments.find((request) => request._id === selectedRequestId);
  const actionableStatuses = ['approved', 'submitted_to_reap', 'failed'];

  return (
    <DashboardLayout sidebar={<AdminSidebar />}>
      <div className="admin-payments-page">
        <div className="page-header">
          <h2>All Payment Requests</h2>
          <p>Manage all international payment requests</p>
        </div>

        <div className="filters-section">
          <div className="filter-group">
            <label htmlFor="status-filter">Filter by Status:</label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="pending_admin_approval">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="processing">Processing Payment</option>
              <option value="rejected">Rejected</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="search">Search:</label>
            <input
              id="search"
              type="text"
              placeholder="Search by company or account number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="payments-container">
          {loading ? (
            <div className="loading">
              <p>Loading payment requests...</p>
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="no-payments">
              <p>No payment requests match your filters.</p>
            </div>
          ) : (
            <table className="payments-table">
              <thead>
                <tr>
                  <th>Recipient</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((request) => {
                  const reapStatus = request.reapStatus;
                  const reapError = request.reapError || request.reapErrorMessage;
                  const reapRemoteStatus =
                    typeof request.reapRawResponse === 'object' && request.reapRawResponse !== null
                      ? ((request.reapRawResponse as any).status || '').toString().toLowerCase()
                      : '';

                  return (
                    <tr key={request._id}>
                      <td className="recipient-info">
                        <div>{request.recipientCompany}</div>
                        <div className="account-number">{request.accountNumber}</div>
                      </td>
                      <td className="amount-info">
                        <div>{formatCurrency(request.foreignAmount, request.foreignCurrency)}</div>
                        <div className="local-amount">{formatCurrency(request.localAmount, 'NGN')}</div>
                      </td>
                      <td>
                        <div className="status-column">
                          <StatusBadge status={request.status} type="payment" />
                          {reapStatus && (
                            <div className="reap-status">
                              <small className={`reap-${reapStatus}`}>Reap: {reapStatus}</small>
                              {reapError && <small className="reap-error-message">{reapError}</small>}
                              {reapStatus === 'not_sent' && !reapError && (
                                <small className="reap-error-message">
                                  Not submitted to Reap yet.
                                </small>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="submitted-date">
                        {request.createdAt ? new Date(request.createdAt).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="actions">
                        {request.status === 'pending_admin_approval' && (
                          <button onClick={() => setSelectedRequestId(request._id!)} className="approve-btn">
                            Review in Drawer
                          </button>
                        )}
                        {actionableStatuses.includes(request.status) && reapStatus === 'sent' && (
                          <button onClick={() => handleDebitWallet(request._id!)} className="complete-btn">
                            Debit Wallet
                          </button>
                        )}
                        {actionableStatuses.includes(request.status) && reapStatus !== 'sent' && (
                          <>
                            <button onClick={() => handleRetryReap(request._id!)} className="retry-btn">
                              Retry Reap
                            </button>
                            <span className="decision-made">Awaiting Reap success</span>
                          </>
                        )}
                        {actionableStatuses.includes(request.status) && reapRemoteStatus === 'draft' && (
                          <button
                            onClick={() => handleReapAction(request._id!, 'accept_quote')}
                            className="approve-btn"
                          >
                            Accept Reap Quote
                          </button>
                        )}
                        {actionableStatuses.includes(request.status) && reapRemoteStatus === 'awaiting_approval' && (
                          <button
                            onClick={() => handleReapAction(request._id!, 'approve')}
                            className="approve-btn"
                          >
                            Approve Reap Payment
                          </button>
                        )}
                        {request.status === 'processing' && (
                          <button onClick={() => handleComplete(request._id!)} className="complete-btn">
                            Mark as Completed
                          </button>
                        )}
                        {request.status !== 'pending_admin_approval' &&
                          request.status !== 'approved' &&
                          request.status !== 'processing' && (
                            <span className="decision-made">{request.status.toUpperCase()}</span>
                          )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <ApprovalWorkflowDrawer
        isOpen={Boolean(selectedRequest)}
        title="Payment Ledger Review"
        subtitle="Capture final approval decision with context from the payment ledger."
        onClose={() => setSelectedRequestId(null)}
        contextItems={
          selectedRequest
            ? [
                { label: 'Recipient', value: selectedRequest.recipientCompany },
                { label: 'Account Number', value: selectedRequest.accountNumber },
                { label: 'Foreign Amount', value: formatCurrency(selectedRequest.foreignAmount, selectedRequest.foreignCurrency) },
                { label: 'Local Amount', value: formatCurrency(selectedRequest.localAmount, 'NGN') },
              ]
            : []
        }
        onApprove={
          selectedRequest
            ? () => handleApprove(selectedRequest._id!)
            : undefined
        }
        onReject={
          selectedRequest
            ? (reason) => handleReject(selectedRequest._id!, reason)
            : undefined
        }
        rejectionLabel="Reason for Rejection"
        rejectionPlaceholder="Explain why this payment request is rejected."
      />
    </DashboardLayout>
  );
};

export default AdminPaymentsPage;
